<script>
import Vue from "vue";

/**
 * Estimated delivery date from the ERP (estimation_arrivee), shown two ways:
 *   layout="inline" one line under the status badge in the orders list
 *   layout="panel"  delivery tracker on the order page
 *
 * Omnipharm only: VUE_APP_FEATURE_ESTIMATION_DELIVERED_ENABLED is set in .env.omn and in no
 * other build profile. Checked here as well as at each call site.
 *
 * States
 *   delivered  the ERP confirmed the delivery                     dark green  "Livrée aujourd'hui à 19:15"
 *   upcoming   not confirmed, estimate still ahead                orange      "Prévue aujourd'hui à 14:00"
 *   overdue    not confirmed, estimate passed less than 24 h ago  orange      "Attendue depuis 12:02"
 *   stale      not confirmed, estimate passed more than 24 h ago  orange      "Estimée le 05/09 à 11:30"
 * A time already passed is never called "prévue". After 24 h the ERP has in practice stopped
 * updating the row, so the wording steps back to a plain reminder rather than an alert.
 * Two colours only, on the list and the order page alike: dark green once delivered, a vivid
 * orange (not the app's navy, which blends into the UI) for
 * every state still waiting for the delivery. The icon shape tells the waiting states apart.
 */

const STALE_AFTER_MS = 24 * 60 * 60 * 1000;

// One clock shared by every instance: a list page renders one of these per row.
const clock = Vue.observable({ now: Date.now() });
let clockStarted = false;
function startClock() {
  if (clockStarted) return;
  clockStarted = true;
  setInterval(() => { clock.now = Date.now(); }, 60 * 1000);
}

const WEEKDAYS = ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."];
const pad = (n) => String(n).padStart(2, "0");

// The API formats dates as "dd/MM/yyyy HH:mm" (Utils.DATE_PATTERN on the server).
function parseApiDate(value) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?/.exec(String(value || "").trim());
  return m ? new Date(+m[3], +m[2] - 1, +m[1], +(m[4] || 0), +(m[5] || 0)) : null;
}

function calendarDaysBetween(a, b) {
  const da = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const db = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((da - db) / 86400000);
}

export default {
  name: "EstimatedDelivery",
  props: {
    date: { type: String, default: null },
    delivered: { type: Boolean, default: false },
    layout: { type: String, default: "inline" },
    orderedAt: { type: String, default: null },
  },
  created() {
    startClock();
  },
  computed: {
    enabled() {
      return process.env.VUE_APP_FEATURE_ESTIMATION_DELIVERED_ENABLED === "true";
    },
    estimate() {
      return parseApiDate(this.date);
    },
    now() {
      return new Date(clock.now);
    },
    state() {
      if (!this.enabled || !this.estimate) return null;
      if (this.delivered) return "delivered";
      const elapsed = this.now.getTime() - this.estimate.getTime();
      if (elapsed < 0) return "upcoming";
      return elapsed < STALE_AFTER_MS ? "overdue" : "stale";
    },
    icon() {
      return { delivered: "bx-check-circle", upcoming: "bxs-truck", overdue: "bx-time-five", stale: "bx-package" }[this.state];
    },
    supplier() {
      return process.env.VUE_APP_CLIENT_FULL_NAME || "le fournisseur";
    },
    time() {
      return pad(this.estimate.getHours()) + ":" + pad(this.estimate.getMinutes());
    },
    shortDate() {
      return pad(this.estimate.getDate()) + "/" + pad(this.estimate.getMonth() + 1);
    },
    dayOffset() {
      return calendarDaysBetween(this.estimate, this.now);
    },
    // "aujourd'hui", "demain", "hier" or "ven. 12/09"
    day() {
      if (this.dayOffset === 0) return "aujourd'hui";
      if (this.dayOffset === 1) return "demain";
      if (this.dayOffset === -1) return "hier";
      return WEEKDAYS[this.estimate.getDay()] + " " + this.shortDate;
    },
    // list: "aujourd'hui", "hier", "le 05/09"   ·   page: "aujourd'hui", "hier", "le ven. 05/09"
    dayShort() {
      return Math.abs(this.dayOffset) <= 1 ? this.day : "le " + this.shortDate;
    },
    dayLong() {
      return Math.abs(this.dayOffset) <= 1 ? this.day : "le " + this.day;
    },
    title() {
      return "Livraison estimée le " + String(this.date).replace(" ", " à ")
        + (this.delivered ? " (livraison confirmée)" : " (réception non confirmée)");
    },
    // orders list line, the time rendered in bold after "lead"
    inline() {
      switch (this.state) {
        case "delivered": return { lead: "Livrée " + this.dayShort + " à ", time: this.time };
        case "upcoming":  return { lead: "Prévue " + this.dayShort + " à ", time: this.time };
        case "overdue":   return { lead: "Attendue depuis " + (this.dayOffset === 0 ? "" : this.dayShort + " à "), time: this.time };
        case "stale":     return { lead: "Estimée " + this.dayShort + " à ", time: this.time };
        default:          return null;
      }
    },
    headline() {
      switch (this.state) {
        case "delivered": return "Commande livrée";
        case "upcoming":  return "Livraison prévue " + this.dayLong + " à " + this.time;
        case "overdue":   return "Livraison attendue depuis " + (this.dayOffset === 0 ? "" : this.dayLong + " à ") + this.time;
        case "stale":     return "Livraison estimée " + this.dayLong + " à " + this.time;
        default:          return "";
      }
    },
    detail() {
      switch (this.state) {
        case "delivered": return "Estimation du " + String(this.date).replace(" ", " à ") + ", livraison confirmée par " + this.supplier + ".";
        case "upcoming":  return "Estimation transmise par " + this.supplier + " après l'expédition de la commande.";
        case "overdue":   return "L'heure estimée est passée ; la réception n'a pas encore été confirmée par " + this.supplier + ".";
        case "stale":     return "La réception n'a pas été confirmée par " + this.supplier + " depuis cette estimation.";
        default:          return "";
      }
    },
    steps() {
      const ordered = parseApiDate(this.orderedAt);
      const orderedHint = ordered
        ? pad(ordered.getDate()) + "/" + pad(ordered.getMonth() + 1) + " " + pad(ordered.getHours()) + ":" + pad(ordered.getMinutes())
        : "";
      const hints = {
        delivered: this.dayShort + " à " + this.time,
        upcoming: "prévue " + this.dayShort + " à " + this.time,
        overdue: "attendue depuis " + (this.dayOffset === 0 ? "" : this.dayShort + " à ") + this.time,
        stale: "estimée " + this.dayShort + " à " + this.time,
      };
      return [
        { key: "ordered", label: "Commandée", hint: orderedHint, status: "done" },
        { key: "shipped", label: "Expédiée", hint: "", status: "done" },
        { key: "delivered", label: "Livrée", hint: hints[this.state], status: this.state === "delivered" ? "done" : "current" },
      ];
    },
  },
};
</script>

<template>
  <span
    v-if="state && layout === 'inline'"
    class="ed-inline"
    :class="'ed-tone--' + state"
    :title="title"
  >
    <i class="bx" :class="icon" aria-hidden="true"></i>{{ inline.lead }}<strong>{{ inline.time }}</strong>
  </span>

  <div
    v-else-if="state && layout === 'panel'"
    class="ed-panel"
    :class="'ed-tone--' + state"
    role="status"
    :title="title"
  >
    <div class="ed-panel__summary">
      <span class="ed-panel__icon">
        <i class="bx" :class="icon" aria-hidden="true"></i>
      </span>
      <div class="ed-panel__text">
        <div class="ed-panel__headline">{{ headline }}</div>
        <div class="ed-panel__detail">{{ detail }}</div>
      </div>
    </div>

    <ol class="ed-steps" aria-label="Suivi de la livraison">
      <li
        v-for="step in steps"
        :key="step.key"
        class="ed-step"
        :class="'ed-step--' + step.status"
      >
        <span class="ed-step__dot" aria-hidden="true"></span>
        <span class="ed-step__label">{{ step.label }}</span>
        <span class="ed-step__hint">{{ step.hint }}</span>
      </li>
    </ol>
  </div>
</template>

<style scoped>
/* two colours: dark green once delivered, vivid orange while waiting (text contrast on white >= 5:1) */
.ed-tone--delivered {
  --ed-fg: #146c43;
  --ed-bg: #e3f4ec;
  --ed-line: #9fd6ba;
}
.ed-tone--upcoming,
.ed-tone--overdue,
.ed-tone--stale {
  --ed-fg: #c2410c;
  --ed-bg: #fff1e6;
  --ed-line: #f4b183;
}

/* ── orders list: one line under the status badge ───────── */
.ed-inline {
  display: block;
  margin-top: 0.3rem;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--ed-fg);
  white-space: nowrap;
  cursor: default;
}
.ed-inline .bx {
  margin-right: 0.3rem;
  font-size: 15px;
  vertical-align: -2px;
  color: var(--ed-fg);
}
.ed-inline strong {
  font-weight: 800;
  color: var(--ed-fg);
}

/* ── order page panel ────────────────────────────────────── */
.ed-panel {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem 2.5rem;
  padding: 0.9rem 1.25rem;
  background: #fff;
  border: 1px solid #dfe2ea;
  border-radius: 0.3rem;
}
.ed-panel__summary {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
}
.ed-panel__icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  font-size: 22px;
  color: var(--ed-fg);
  background: var(--ed-bg);
  border: 1px solid var(--ed-line);
  border-radius: 50%;
}
.ed-panel__headline {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--ed-fg);
}
.ed-panel__detail {
  margin-top: 2px;
  font-size: 13px;
  color: #4d5361;
}

/* three-step tracker: Commandée → Expédiée → Livrée */
.ed-steps {
  display: flex;
  flex: 0 1 440px;
  min-width: 270px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.ed-step {
  position: relative;
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  font-size: 12.5px;
}
/* connector drawn from the previous dot to this one */
.ed-step::before {
  content: "";
  position: absolute;
  top: 7px;
  left: -50%;
  width: 100%;
  height: 2px;
  background: #cfd3dd;
}
.ed-step:first-child::before {
  display: none;
}
.ed-step__dot {
  position: relative;
  z-index: 1;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #b8bdca;
}
.ed-step--done .ed-step__dot {
  background: #1a7f4f;
  border-color: #1a7f4f;
}
.ed-step--done::before {
  background: #1a7f4f;
}
/* last leg still in progress: dashed, dot ringed in the state colour */
.ed-step--current::before {
  height: 0;
  background: none;
  border-top: 2px dashed #b8bdca;
}
.ed-step--current .ed-step__dot {
  border-color: var(--ed-fg);
  box-shadow: 0 0 0 4px var(--ed-bg);
}
.ed-step__label {
  margin-top: 0.45rem;
  font-weight: 700;
  color: #2f343d;
}
.ed-step__hint {
  min-height: 1.2em;
  font-size: 12px;
  color: #4d5361;
  white-space: nowrap;
}
.ed-step--current .ed-step__hint {
  font-weight: 600;
  color: var(--ed-fg);
}
@media (max-width: 575.98px) {
  .ed-steps {
    flex-basis: 100%;
  }
}
</style>
